import React, { useMemo, useCallback } from 'react'
import { Row, Col } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { CompanyTeamTable } from '../../../../../core/components/CompanyTeamTable/index.tsx'
import { transformExCompanyTeamToTeamMembers } from '../../../../../core/components/CompanyTeamTable/utils/utils.ts'
import { OtherTable } from '../../tables/ContactsTables/OtherTable.tsx'
import { AccountRecord, AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { CompanyTeamEntryFields } from '../../../../../state_manager/data_models/company_team_entry.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import { RELATIONSHIP_TYPE_ADVISOR, RELATIONSHIP_TYPE_OTHER } from '../../../../../state_manager/globals.ts'
import { ACTION_TYPE_INSERT, ACTION_TYPE_UPDATE, ACTION_TYPE_DELETE } from '../../../../../state_manager/globals.ts'
import { getAuthHeaders } from '../../../../../core/lib/apiRequest.js'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../../../core/utils/endpoints.ts'
import { fetchCompanyById } from '../../../services/companiesService.ts'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import {
  TOAST_CONFIG,
  TOAST_AUTO_CLOSE,
  TOAST_INFO_CONFIG
} from '../../../../../core/components/FormTable/constants.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'

function parseContactName(name: string): { first_name: string | null; last_name: string } {
  if (!name || typeof name !== 'string') {
    return { first_name: null, last_name: '' }
  }
  const cleanName = name.replace(/\s*\(new\)\s*$/i, '').trim()
  if (cleanName.length === 0) {
    return { first_name: null, last_name: '' }
  }
  const nameParts = cleanName.split(/\s+/).filter((part) => part.length > 0)

  if (nameParts.length === 0) {
    return { first_name: null, last_name: '' }
  }

  if (nameParts.length === 1) {
    return { first_name: null, last_name: nameParts[0] }
  }

  return {
    first_name: nameParts[0],
    last_name: nameParts.slice(1).join(' ')
  }
}

export function ContactsTab({
  formData,
  currentCompany,
  companyTeamMembers = [],
  handleFieldChange,
  handleSaveField,
  recordId
}: {
  formData?: Record<string, any>
  currentCompany?: AccountRecord | null
  companyTeamMembers?: any[]
  handleFieldChange?: (field: string, value: any) => void
  handleSaveField?: (field: string, value: any) => void
  recordId?: string | null
}) {
  const dispatch = useDispatch()
  const data = currentCompany || formData || {}
  const relationshipCategories = useSelector((state: any) => state.globalConfigs?.relationship_categories || [])

  const exCompanyTeamRaw = data[AccountRecordFields.prog__ex_company_team]
  const otherTeamRaw = data[AccountRecordFields.prog__other_team]

  const exCompanyTeamKey = useMemo(() => {
    if (!exCompanyTeamRaw || !Array.isArray(exCompanyTeamRaw)) {
      return exCompanyTeamRaw === undefined ? undefined : '[]'
    }
    return JSON.stringify(
      exCompanyTeamRaw.map((m: any) => ({
        id: m[CompanyTeamEntryFields.contact_accounts__id],
        contactId: m[CompanyTeamEntryFields.contacts__id]
      }))
    )
  }, [exCompanyTeamRaw])

  const otherTeamKey = useMemo(() => {
    if (!otherTeamRaw || !Array.isArray(otherTeamRaw)) {
      return otherTeamRaw === undefined ? undefined : '[]'
    }
    return JSON.stringify(
      otherTeamRaw.map((m: any) => ({
        id: m[CompanyTeamEntryFields.contact_accounts__id],
        contactId: m[CompanyTeamEntryFields.contacts__id]
      }))
    )
  }, [otherTeamRaw])

  const exCompanyTeam = Array.isArray(exCompanyTeamRaw) ? exCompanyTeamRaw : []
  const otherTeam = Array.isArray(otherTeamRaw) ? otherTeamRaw : []

  const processCompanyTeamRelationships = useCallback(async (companyId: string, relationships: any[]) => {
    try {
      const BASE_URL = getBackendBaseUrl()
      const headers = await getAuthHeaders()

      const response = await fetch(buildFullUrl(BASE_URL, API_ROUTES.CONTACT_RELATIONSHIPS.PROCESS_TEAM), {
        method: API_ROUTES.CONTACT_RELATIONSHIPS.PROCESS_TEAM.method,
        headers: {
          ...headers,
          'account-id': companyId
        },
        body: JSON.stringify(relationships)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error calling contact relationships API:', error)
      throw error
    }
  }, [])

  const handleOtherTableSave = useCallback(
    async (savedEntry: any, allEntries: any[]) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!recordId || !currentToken) {
        console.warn('Cannot save other table entries: Company ID or access token not available')
        showErrorToast('Cannot save other table entries: Company ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const getTypeIdFromCategoryId = (categoryId: string | null | undefined): string | null => {
          if (!categoryId) return null
          const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
          return category?.relationship_type_id || category?.relationshipTypeId || null
        }

        const currentOtherTeam = otherTeam.filter((entry: any) => {
          const categoryId = entry[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
          if (!categoryId) return false
          const typeId = getTypeIdFromCategoryId(categoryId)
          return typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER
        })

        const currentEntryMap = new Map<string, any>()

        currentOtherTeam.forEach((entry: any) => {
          const contactId =
            entry[UserEntryFields.contacts__id] || entry[CompanyTeamEntryFields.contacts__id] || entry.id
          const categoryId = entry[CompanyTeamEntryFields.contact_accounts__relationship_category_id]

          const typeId = getTypeIdFromCategoryId(categoryId)
          if (typeId !== RELATIONSHIP_TYPE_ADVISOR && typeId !== RELATIONSHIP_TYPE_OTHER) {
            return
          }

          const relationshipId =
            entry[UserEntryFields.contact_accounts__id] ||
            entry[CompanyTeamEntryFields.contact_accounts__id] ||
            entry.contact_accounts__id

          if (contactId && categoryId && relationshipId) {
            currentEntryMap.set(`${contactId}-${categoryId}`, { contactId, relationshipId, categoryId })
          }
        })

        const newEntryMap = new Map<string, any>()
        allEntries
          .filter((entry) => {
            if (!entry.contactId || !entry.relationshipCategoryId) return false
            const typeId = getTypeIdFromCategoryId(entry.relationshipCategoryId)
            return typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER
          })
          .forEach((entry) => {
            const contactId = entry.contactId
            const categoryId = entry.relationshipCategoryId
            newEntryMap.set(`${contactId}-${categoryId}`, entry)
          })

        const allRelationships: any[] = []
        const relationshipsToDelete = new Set<string>()

        allEntries
          .filter((entry) => {
            if (!entry.contactId || !entry.relationshipCategoryId) return false
            const typeId = getTypeIdFromCategoryId(entry.relationshipCategoryId)
            return typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER
          })
          .forEach((entry) => {
            const isNewEntry = entry.key?.startsWith('new-') || !entry.relationshipId
            const relationshipId = isNewEntry ? null : entry.relationshipId

            let categoryChanged = false
            let oldRelationshipIdToDelete: string | null = null

            if (relationshipId && !isNewEntry) {
              for (const [key, currentEntry] of currentEntryMap.entries()) {
                if (currentEntry.relationshipId === relationshipId) {
                  const expectedKeyPrefix = `${entry.contactId}-`

                  if (key.startsWith(expectedKeyPrefix)) {
                    const currentCategoryId = key.substring(expectedKeyPrefix.length)
                    if (currentCategoryId !== entry.relationshipCategoryId) {
                      categoryChanged = true
                      oldRelationshipIdToDelete = relationshipId
                      relationshipsToDelete.add(relationshipId)
                      break
                    }
                  }
                }
              }
            }

            let action = ''
            if (isNewEntry || categoryChanged) {
              action = ACTION_TYPE_INSERT
            } else if (relationshipId) {
              action = ACTION_TYPE_UPDATE
            }

            if (action) {
              const isNewContact = entry.contactId === 'new'
              let contactId = entry.contactId

              if (isNewContact) {
                contactId = crypto.randomUUID()
              }

              const relationshipPayload: any = {
                [CompanyTeamEntryFields.contacts__id]: contactId,
                [CompanyTeamEntryFields.contact_accounts__id]: categoryChanged ? null : relationshipId,
                [CompanyTeamEntryFields.contact_accounts__account_id]: recordId,
                [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: entry.relationshipCategoryId,
                [CompanyTeamEntryFields.contact_accounts__current]: true,
                [CompanyTeamEntryFields.prog__contact_accounts__action]: action
              }

              if (isNewContact) {
                const { first_name, last_name } = parseContactName(entry.contactName || '')

                relationshipPayload[CompanyTeamEntryFields.contacts__first_name] = first_name
                relationshipPayload[CompanyTeamEntryFields.contacts__last_name] = last_name
                if (entry.email) {
                  relationshipPayload[CompanyTeamEntryFields.contacts__email] = entry.email
                }
                relationshipPayload[CompanyTeamEntryFields.prog__contacts__action] = ACTION_TYPE_INSERT
              } else {
                if (entry.email) {
                  relationshipPayload[CompanyTeamEntryFields.contacts__email] = entry.email
                  const originalEntry = entry.originalEntry
                  const originalEmail =
                    originalEntry?.[UserEntryFields.contacts__email] ||
                    originalEntry?.[CompanyTeamEntryFields.contacts__email]
                  if (action === ACTION_TYPE_UPDATE || entry.email !== originalEmail) {
                    relationshipPayload[CompanyTeamEntryFields.prog__contacts__action] = ACTION_TYPE_UPDATE
                  }
                }
              }

              allRelationships.push(relationshipPayload)
            }

            if (categoryChanged && oldRelationshipIdToDelete) {
              allRelationships.push({
                [CompanyTeamEntryFields.contact_accounts__id]: oldRelationshipIdToDelete,
                [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
              })
            }
          })

        currentEntryMap.forEach((currentEntry, key) => {
          if (!newEntryMap.has(key) && !relationshipsToDelete.has(currentEntry.relationshipId)) {
            relationshipsToDelete.add(currentEntry.relationshipId)
            allRelationships.push({
              [CompanyTeamEntryFields.contact_accounts__id]: currentEntry.relationshipId,
              [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
            })
          }
        })

        if (allRelationships.length === 0) {
          return
        }

        const result = await processCompanyTeamRelationships(recordId, allRelationships)

        if (!result || result.error) {
          showErrorToast(result?.error || 'Failed to update other contacts', TOAST_CONFIG)
          return
        }

        const response = await fetchCompanyById(recordId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateCompanyRecord({ companyId: recordId, updates: response.data }))
        }

        toast.success('Other contacts updated successfully', TOAST_INFO_CONFIG)
      } catch (error) {
        console.error('Error updating other table entries:', error)
        showErrorToast('Failed to update other contacts', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [recordId, otherTeam, otherTeamKey, dispatch, processCompanyTeamRelationships, relationshipCategories]
  )

  const handleOtherTableDelete = useCallback(
    async (entry: any) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!recordId || !currentToken) {
        console.warn('Cannot delete other table entry: Company ID or access token not available')
        showErrorToast('Cannot delete other table entry: Company ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const relationshipId = entry.relationshipId || entry.key

        if (!relationshipId || relationshipId.startsWith('new-')) {
          return
        }

        const deletePayload = [
          {
            [CompanyTeamEntryFields.contact_accounts__id]: relationshipId,
            [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
          }
        ]

        await processCompanyTeamRelationships(recordId, deletePayload)

        const response = await fetchCompanyById(recordId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateCompanyRecord({ companyId: recordId, updates: response.data }))
        }

        toast.success('Entry deleted successfully', TOAST_INFO_CONFIG)
      } catch (error) {
        console.error('Error deleting other table entry:', error)
        showErrorToast('Failed to delete entry', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        throw error
      }
    },
    [recordId, dispatch, processCompanyTeamRelationships]
  )

  const handleTeamTableChange = useCallback(async () => {
    if (!recordId) {
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (!currentToken) {
      return
    }

    try {
      const response = await fetchCompanyById(recordId, currentToken)
      if (response.data && !response.error) {
        dispatch(updateCompanyRecord({ companyId: recordId, updates: response.data }))
      }
    } catch (error) {
      console.error('Error refreshing company data after team table change:', error)
    }
  }, [recordId, dispatch])

  const companyTeamMembersKey = useMemo(() => {
    if (!companyTeamMembers || !Array.isArray(companyTeamMembers) || companyTeamMembers.length === 0) {
      return companyTeamMembers === undefined ? undefined : '[]'
    }
    return JSON.stringify(
      companyTeamMembers.map((m: any) => ({
        key: m.key,
        founder: m.founder,
        current: m.current,
        category: m.category,
        contactId: m.contact?.value
      }))
    )
  }, [companyTeamMembers])

  const foundersMembers = useMemo(() => {
    return companyTeamMembers.filter((member) => member.founder === true)
  }, [companyTeamMembers, companyTeamMembersKey])

  const employeesMembers = useMemo(() => {
    const getTypeIdFromCategoryId = (categoryId: string | null | undefined): string | null => {
      if (!categoryId) return null
      const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
      return category?.relationship_type_id || category?.relationshipTypeId || null
    }

    return companyTeamMembers.filter((member) => {
      if (member.founder === true) {
        return false
      }

      if (member.current !== true) {
        return false
      }

      const typeId = getTypeIdFromCategoryId(member.category)
      if (typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER) {
        return false
      }

      return true
    })
  }, [companyTeamMembers, companyTeamMembersKey, relationshipCategories])

  const exEmployeesMembers = useMemo(() => {
    return transformExCompanyTeamToTeamMembers(exCompanyTeam, relationshipCategories)
  }, [exCompanyTeam, exCompanyTeamKey, relationshipCategories])

  return (
    <div
      data-debug='company-contacts-tab-container'
      data-component='ContactsTab'
      data-record-id={recordId}
      className='h-full overflow-y-auto overflow-x-hidden'
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <CompanyTeamTable
            key={`founders-${companyTeamMembersKey || 'empty'}`}
            mode='existing'
            companyId={recordId}
            initialTeamMembers={foundersMembers}
            readOnly={false}
            title='Founders'
            hideFounderColumn={true}
            enforcedFounderStatus={true}
            hideCategoryColumn={true}
            hideJobTitleColumn={true}
            nameColumnEditable={false}
            showDeleteOnly={true}
            onChange={handleTeamTableChange}
          />
        </Col>

        <Col span={24}>
          <CompanyTeamTable
            key={`employees-${companyTeamMembersKey || 'empty'}`}
            mode='existing'
            companyId={recordId}
            initialTeamMembers={employeesMembers}
            readOnly={false}
            title='Employees'
            hideFounderColumn={true}
            enforcedFounderStatus={false}
            nameColumnEditable={false}
            onChange={handleTeamTableChange}
          />
        </Col>

        <Col span={24}>
          <OtherTable
            key={otherTeamKey || 'other-team-empty'}
            otherTeam={otherTeam}
            recordId={recordId}
            onSave={handleOtherTableSave}
            onDelete={handleOtherTableDelete}
          />
        </Col>

        <Col span={24}>
          <CompanyTeamTable
            key={exCompanyTeamKey || 'ex-employees-empty'}
            mode='existing'
            companyId={recordId}
            initialTeamMembers={exEmployeesMembers}
            readOnly={false}
            title='Ex-employees'
            hideFounderColumn={true}
            hideRequiredMarks={true}
            nameColumnEditable={false}
            showDeleteOnly={true}
            isExEmployees={true}
            onChange={handleTeamTableChange}
          />
        </Col>
      </Row>
    </div>
  )
}
